import { APIResponse, defaultMutationFn } from 'api';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Folio, VelocityEngageFolioFolderDocument } from 'models';

export function useFolio(vehicleId: string) {
  const path = `/inventory/${vehicleId}/plugins/vdp/velocityengage/folio`;
  return useQuery<APIResponse<Folio>>(path, {
    enabled: vehicleId !== undefined,
  });
}

export function useCreateFolioDocument() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      vehicleId,
      folioFolderId,
      url,
      displayLabel,
    }: {
      vehicleId: string;
      folioFolderId: string;
      url: string;
      displayLabel: string;
    }) => {
      const path = `/inventory/${vehicleId}/plugins/vdp/velocityengage/folio/document`;
      const data = {
        folioFolderId,
        url,
        displayLabel,
      };
      return defaultMutationFn(path, 'POST', data);
    },
    {
      onSuccess: async (data, variables) => {
        await queryClient.setQueryData(
          `/inventory/${variables.vehicleId}/plugins/vdp/velocityengage/folio`,
          data
        );
      },
    }
  );
}

export function useDeleteFolioDocument() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      vehicleId,
      document,
    }: {
      vehicleId: string;
      document: VelocityEngageFolioFolderDocument;
    }) => {
      const path = `/inventory/${vehicleId}/plugins/vdp/velocityengage/folio/document/${document.id}`;
      return defaultMutationFn(path, 'DELETE');
    },
    {
      onSuccess: async (data, variables) => {
        await queryClient.setQueryData(
          `/inventory/${variables.vehicleId}/plugins/vdp/velocityengage/folio`,
          data
        );
      },
    }
  );
}

export function useFeatureFolioDocument() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      vehicleId,
      document,
    }: {
      vehicleId: string;
      document: VelocityEngageFolioFolderDocument;
    }) => {
      const path = `/inventory/${vehicleId}/plugins/vdp/velocityengage/folio/document/${document.id}/feature`;
      return defaultMutationFn(path, 'POST');
    },
    {
      onSuccess: async (data, variables) => {
        await queryClient.setQueryData(
          `/inventory/${variables.vehicleId}/plugins/vdp/velocityengage/folio`,
          data
        );
      },
    }
  );
}

export function useUnfeatureFolioDocument() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      vehicleId,
      document,
    }: {
      vehicleId: string;
      document: VelocityEngageFolioFolderDocument;
    }) => {
      const path = `/inventory/${vehicleId}/plugins/vdp/velocityengage/folio/document/${document.id}/feature`;
      return defaultMutationFn(path, 'DELETE');
    },
    {
      onSuccess: async (data, variables) => {
        await queryClient.setQueryData(
          `/inventory/${variables.vehicleId}/plugins/vdp/velocityengage/folio`,
          data
        );
      },
    }
  );
}

export function useShowFolioDocument() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      vehicleId,
      document,
    }: {
      vehicleId: string;
      document: VelocityEngageFolioFolderDocument;
    }) => {
      const path = `/inventory/${vehicleId}/plugins/vdp/velocityengage/folio/document/${document.id}/visible`;
      return defaultMutationFn(path, 'POST');
    },
    {
      onSuccess: async (data, variables) => {
        await queryClient.setQueryData(
          `/inventory/${variables.vehicleId}/plugins/vdp/velocityengage/folio`,
          data
        );
      },
    }
  );
}

export function useHideFolioDocument() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      vehicleId,
      document,
    }: {
      vehicleId: string;
      document: VelocityEngageFolioFolderDocument;
    }) => {
      const path = `/inventory/${vehicleId}/plugins/vdp/velocityengage/folio/document/${document.id}/visible`;
      return defaultMutationFn(path, 'DELETE');
    },
    {
      onSuccess: async (data, variables) => {
        await queryClient.setQueryData(
          `/inventory/${variables.vehicleId}/plugins/vdp/velocityengage/folio`,
          data
        );
      },
    }
  );
}

export function useUpdateFolioDocumentName() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      vehicleId,
      document,
      name,
    }: {
      vehicleId: string;
      document: VelocityEngageFolioFolderDocument;
      name: string;
    }) => {
      const path = `/inventory/${vehicleId}/plugins/vdp/velocityengage/folio/document/${document.id}`;
      const data = { displayLabel: name };
      return defaultMutationFn(path, 'PUT', data);
    },
    {
      onSuccess: async (data, variables) => {
        await queryClient.setQueryData(
          `/inventory/${variables.vehicleId}/plugins/vdp/velocityengage/folio`,
          data
        );
      },
    }
  );
}

export function useReorderFolioDocuments() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      vehicleId,
      reorderObject,
    }: {
      vehicleId: string;
      reorderObject: Record<string, number>;
    }) => {
      const path = `/inventory/${vehicleId}/plugins/vdp/velocityengage/folio/document/reorder`;
      return defaultMutationFn(path, 'PUT', reorderObject);
    },
    {
      onSuccess: async (data, variables) => {
        await queryClient.setQueryData(
          `/inventory/${variables.vehicleId}/plugins/vdp/velocityengage/folio`,
          data
        );
      },
    }
  );
}
